import { WELL_FIELDS } from '@apps/components/DataViewer/WellDataViewer/wellApiConfig/wellSchema';
import {ALL_RECORDS} from '@config/config';
import { log } from '@libs/utils/helper.js';
import { LOG_MODULES } from "@config/config";
/**
 * Maps UI tokens to the exact ArcGIS Service field names and data types
 * based on the NIOGEMS_v5 Wells Layer metadata.
 */

export const mapUiToArcGisField = (uiToken) => {
  const t = (uiToken || "").toLowerCase();

  // Explicit schema-driven mappings
  const explicit = {
    [WELL_FIELDS.Operator.toLowerCase()]: { arcgis: "OPERATOR_NAME", kind: "string" },
    [WELL_FIELDS.LeaseName.toLowerCase()]: { arcgis: "LEASE_NAME", kind: "string" },
    [WELL_FIELDS.FieldName.toLowerCase()]: { arcgis: "FIELD_NAME", kind: "string" },
  };

  if (explicit[t]) return explicit[t];

  // API number (catch both the schema constant and variations like "api")
  if (t === WELL_FIELDS.ApiNo.toLowerCase() || t.includes("api")) {
    return { arcgis: "API_NUMBER", kind: "string" };
  }

  // Completion Date (schema-aligned)
  if (t === WELL_FIELDS.ComplDate.toLowerCase() || t.includes("completion_date")) {
    return { arcgis: "COMPLETION_DATE", kind: "dateOnly" };
  }

  // Numeric cumulative fields — schema driven
  if (t === WELL_FIELDS.TotalOil.toLowerCase() || t === WELL_FIELDS.CumOil?.toLowerCase()) {
    return { arcgis: "CUM_OIL", kind: "integer" };
  }
  if (t === WELL_FIELDS.TotalGas.toLowerCase() || t === WELL_FIELDS.CumGas?.toLowerCase()) {
    return { arcgis: "CUM_GAS", kind: "integer" };
  }
  if (t === WELL_FIELDS.TotalDaysOnProd.toLowerCase()) {
    return { arcgis: "CUM_DAYS_ON_PRODUCTION", kind: "integer" };
  }

  // Default passthrough
  return { arcgis: uiToken.toUpperCase(), kind: "string" };
};

// --- Internal Helper Functions ---

const escapeSql = (val) => (val == null ? '' : String(val).replace(/'/g, "''").trim());

const quote = (val) => `'${escapeSql(val)}'`;

const dateLiteral = (yyyyMmDd) => `DATE '${escapeSql(yyyyMmDd)}'`;
// --- NEW: Checked-selection clause builder ---
/**
 * Builds a `FIELD IN ('a','b','c')` clause from an array of checked well IDs.
 * Returns null if there's no field or no ids — so callers can safely
 * pass the result straight into combineSqlClauses without extra checks.
 */
export const buildCheckedIdsClause = (field, ids = []) => {
  if (!field || !Array.isArray(ids) || ids.length === 0) return null;
  const quoted = ids.map((id) => quote(id)).join(', ');
  return `${field} IN (${quoted})`;
};

/**
 * ANDs together any number of SQL clauses, dropping empty/null/ALL_RECORDS
 * entries so the combined expression stays as clean as possible.
 * Returns ALL_RECORDS if nothing meaningful is left.
 */
export const combineSqlClauses = (...clauses) => {
  const meaningful = clauses.filter((c) => c && c !== ALL_RECORDS);
  return meaningful.length > 0 ? meaningful.join(' AND ') : ALL_RECORDS;
};
// --- Clause Builders ---

const buildTextClause = (field, val, mode, ci) => {
  const f = ci ? `UPPER(${field})` : field;
  const v = ci ? String(val).toUpperCase() : val;

  switch (mode) {
    case 'contains': return `${f} LIKE ${quote(`%${v}%`)}`;
    case 'startswith': return `${f} LIKE ${quote(`${v}%`)}`;
    case 'equals':
    default: return `${f} = ${quote(v)}`;
  }
};

const buildNumericClause = (field, val, mode) => {
  const n = Number(val);
  if (isNaN(n)) return null;
  if (mode === 'greaterThan') return `${field} > ${n}`;
  if (mode === 'lessThan') return `${field} < ${n}`;
  return `${field} = ${n}`;
};



/**
 * Main function to convert SearchPanel params into an ArcGIS SQL string.
 * * @param {Object} qp - The dynamic user search parameters (searchText, category, etc.)
 * @param {Object} options - Business logic flags (currentCompl, includeDeleted) //will remove it later
 */
export function buildSqlExpression(qp, { currentCompl = 'Y', includeDeleted = false } = {}) {
  //console.log('[buildSqlExpression] input qp:', qp); // ← DEBUG
  log(LOG_MODULES.WELLSEARCH, "[sqlUtils] buildSqlExpression:", qp);

  if (!qp && !currentCompl && includeDeleted) return ALL_RECORDS;

  const clauses = [];
  const ci = qp?.caseInsensitive ?? true;

  if (!includeDeleted) {
    clauses.push("DELETED = 0");
  }

  if (qp?.searchText && qp?.searchCategory) {
    const { arcgis, kind } = mapUiToArcGisField(qp.searchCategory);
    console.log('[buildSqlExpression] category branch:', { searchText: qp.searchText, searchCategory: qp.searchCategory, arcgis, kind }); // ← DEBUG

    if (kind === 'string') {
      clauses.push(buildTextClause(arcgis, qp.searchText, qp.searchMode, ci));
    } else if (kind === 'integer' || kind === 'double') {
      clauses.push(buildNumericClause(arcgis, qp.searchText, qp.searchMode));
    }
  }

  if (qp?.status && qp.status !== "") {
    console.log('[buildSqlExpression] status branch, raw qp.status:', JSON.stringify(qp.status)); // ← DEBUG
    clauses.push(`CURRENT_STATUS = ${quote(qp.status)}`);
  }

  // --- 4. Date Logic (Field: COMPLETION_DATE) ---

  const dateField = 'COMPLETION_DATE';
  const rawDate = qp?.completion_date || qp?.[WELL_FIELDS.ComplDate];
  
  const parsedSingleDate = parseDateString(rawDate);
  const parsedFrom = parseDateString(qp?.completion_date_from);
  const parsedTo = parseDateString(qp?.completion_date_to);

  const dateClause = buildDateClause(
    dateField,
    qp?.completionDateMode,
    parsedSingleDate,
    parsedFrom,
    parsedTo
  );
  
  if (dateClause) clauses.push(dateClause);

  // Join all parts with AND. If empty, fallback to 1=1 to prevent API errors.
  const finalSql = clauses.filter(Boolean).join(" AND ");
  console.log('[buildSqlExpression] final clauses:', clauses, '-> finalSql:', finalSql); // ← DEBUG
  return finalSql || ALL_RECORDS;
}



/**
 * Converts a JS Date or Date String to ArcGIS SQL format: DATE 'YYYY-MM-DD'
 */

function buildDateClause(field, mode, singleDate, fromDate, toDate) {
  const dateVal = formatToArcGisDate(singleDate);
  const start = formatToArcGisDate(fromDate);
  const end = formatToArcGisDate(toDate);

  switch (mode) {
    case "equals":
      return dateVal ? `${field} = ${dateVal}` : null;
    case "before":
      return dateVal ? `${field} < ${dateVal}` : null;
    case "after":
      return dateVal ? `${field} > ${dateVal}` : null;
    case "between":
      return (start && end) ? `${field} BETWEEN ${start} AND ${end}` : null;
    default:
      return null;
  }
};

/**
 * STEP 1: Parse what the user typed (MM/DD/YYYY) into a JS Date
 */
const parseDateString = (str) => {
  if (!str) return null;

  // If it's already YYYY-MM-DD (from the service)
  if (str.includes('-')) return str;

  // Handle User Input: MM/DD/YYYY
  const parts = str.split('/');
  if (parts.length === 3) {
    const m = parts[0].padStart(2, '0');
    const d = parts[1].padStart(2, '0');
    let y = parts[2];

    if (y.length === 2) {
      y = '19' + y;
    }

    // Return a clean ISO string manually built, NO 'new Date()'
    return `${y}-${m}-${d}`;
  }

  return str;
};

/**
 * STEP 2: Convert that JS Date into the ArcGIS SQL format
 * This ensures the "MM/DD/YYYY" input matches the "YYYY-MM-DD" database field
 */
/**
 * Converts a JS Date to ArcGIS SQL format: DATE 'YYYY-MM-DD'
 * Uses Local time methods to prevent timezone day-shifting.
 */
const formatToArcGisDate = (dateStr) => {
  if (!dateStr) return null;

  // If it's already a string from our parser, just verify it's not 'Invalid Date'
  // and wrap it in the ArcGIS DATE literal format
  if (typeof dateStr === 'string' && dateStr.includes('-')) {
     return `DATE '${dateStr}'`;
  }

  // Emergency fallback for actual Date objects
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `DATE '${yyyy}-${mm}-${dd}'`;
  } catch (e) {
    return null;
  }
};
